import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, OutputRefSubscription, SimpleChanges } from '@angular/core';
import { WebStorageService } from '../../services/web-storage.service';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ConfirmationTypeEnum } from 'src/app/shared/utils/confirmation-popup-enum';
import { NgIf, NgFor, NgStyle } from '@angular/common';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { CurrencyAmountPipe } from '../../pipes/currency-amount.pipe';
import { ReadableDatePipe } from '../../pipes/readable-date.pipe';
import { UiTerminologyPipe } from '../../pipes/ui-terminology.pipe';


import { ImageFallbackDirective } from '../../directives/image-fallback.directive';
import { PinataThumbnailPipe } from 'src/app/shared/pipes/pinata-thumbnail.pipe';
import { ProgressAssetSummary, summariseProgressAsset, withStop } from 'src/app/shared/utils/progress-asset.util';

/** One line of the terms list beside the review and processing steps. */
export interface ConfirmationTermRow {
    label: string;
    value: string;
    /** Total line: drawn under a hairline in medium weight. */
    isTotal?: boolean;
}

/** Review-step heading per confirmation type; a flow can pass confirmationData.review_title instead. */
const REVIEW_TITLES: Record<string, string> = {
    [ConfirmationTypeEnum.LOAN]: 'Review loan request.',
    [ConfirmationTypeEnum.COUNTER]: 'Review counteroffer.',
    [ConfirmationTypeEnum.SALE]: 'Review sell order.',
    [ConfirmationTypeEnum.BUY]: 'Review purchase.',
    [ConfirmationTypeEnum.BID]: 'Review bid.',
    [ConfirmationTypeEnum.ACCEPT_BID]: 'Review bid acceptance.',
    [ConfirmationTypeEnum.REPAY]: 'Review repayment.',
    [ConfirmationTypeEnum.RESALE]: 'Review resale.',
    [ConfirmationTypeEnum.REFINANCE]: 'Review refinance.',
    [ConfirmationTypeEnum.RESALE_COUNTER]: 'Review counteroffer.',
    [ConfirmationTypeEnum.FORECLOSE]: 'Review foreclosure.',
    [ConfirmationTypeEnum.CANCEL]: 'Review cancellation.',
    [ConfirmationTypeEnum.TRANSFER_NFT]: 'Review transfer.',
    [ConfirmationTypeEnum.OFFER_LOAN]: 'Review offer acceptance.',
};

/** Confirmation types whose terms are the loan's (amount, duration, APR …). */
const LOAN_TERM_TYPES: string[] = [
    ConfirmationTypeEnum.LOAN,
    ConfirmationTypeEnum.COUNTER,
    ConfirmationTypeEnum.REFINANCE,
    ConfirmationTypeEnum.REPAY,
    ConfirmationTypeEnum.OFFER_LOAN,
];

@Component({
    selector: 'app-confirmation-modal',
    templateUrl: './confirmation-modal.component.html',
    styleUrls: ['./confirmation-modal.component.css'],
    imports: [PinataThumbnailPipe, ImageFallbackDirective,
        NgIf,
        NgFor,
        NgStyle,
        SafeHtmlPipe,
        CurrencyAmountPipe,
        ReadableDatePipe,
        UiTerminologyPipe,
    ]
})
export class ConfirmationModalComponent
    implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
    ConfirmationTypeEnum = ConfirmationTypeEnum;
    @Input() hasInactiveItems: boolean = false;
    @Input() loanRequestData: any;
    @Input() confirmationData: any;
    @Output() closeModal = new EventEmitter();
    @Output() confirmPurchase = new EventEmitter();
    imageLoading: boolean = true;
    confirmRequest: boolean = false;

    @Input() content: string = ''; //Dynamic content to represent.
    @Input() title: string = ''; //Set the custom title.

    @Input() progressData: any = {};
    @Input() modalRef!: ModalDirective;
    @Input() showEstimation: boolean = false;
    isRegulated: boolean = false;
    isDataLoaded = false;
    // Set on the first Confirm click so repeated clicks cannot start the same
    // transaction flow again while the parent is still running its pre-checks.
    isConfirming: boolean = false;

    minutes: string = '00';
    seconds: string = '00';
    milliseconds: string = '00';
    countdownEnded: boolean = false;

    private totalDurationInS!: number;
    private totalDurationInMs!: number;
    private endTime!: number;
    private intervalId!: NodeJS.Timeout;
    private modalHiddenSubscription?: OutputRefSubscription;
    private hasSubscribedToModalEvents: boolean = false;

    private readonly currencyAmount = new CurrencyAmountPipe();
    private readonly readableDate = new ReadableDatePipe();

    /** Adds the app's closing full stop to a value that does not already end in punctuation. */
    withStop = withStop;

    constructor(private webStorageService: WebStorageService) {}

    ngOnInit(): void {
        this.isRegulated = JSON.parse(
            this.webStorageService.getLocalStorage('regulated') || 'true'
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes['confirmationData'] &&
            !changes['confirmationData'].firstChange
        ) {
            changes['confirmationData'].currentValue?.image?.map(
                (image: any, index: number) => {
                    changes['confirmationData'].currentValue.image[index] = {
                        image,
                    };
                }
            );
        }
        // A new confirmation is being shown - allow Confirm again.
        if (changes['confirmationData']) {
            this.isConfirming = false;
        }
        
        const hasProgressData = !!(
            this.progressData && Object.keys(this.progressData).length > 0
        );

        // Handle progressData changes - only update data, don't create subscriptions here
        if (changes['progressData']) {
            if (hasProgressData) {
                if (this.showEstimation && !this.confirmRequest) {
                    this.startCountDown(60);
                    this.confirmRequest = true;
                }
            } else {
                this.resetProgressState();
            }
        }
        
        // Update isDataLoaded based on progressData
        this.isDataLoaded = hasProgressData;
    }

    ngAfterViewInit(): void {
        // Subscribe to modal events only once
        this.subscribeToModalEvents();
        // this.modalRef?.onShown.subscribe(() => {
        //     if (this.showEstimation) this.startCountDown(60);
        // });
        // this.modalRef?.onHidden.subscribe(() => {
        //     this.resetTimer();
        //     this.countdownEnded = false;
        // });
    }

    /**
     * Subscribes to modal events (onHidden) only once to prevent multiple subscriptions
     */
    private subscribeToModalEvents(): void {
        if (this.modalRef && !this.hasSubscribedToModalEvents) {
            this.modalHiddenSubscription = this.modalRef.onHidden.subscribe(() => {
                this.resetProgressState();
            });
            this.hasSubscribedToModalEvents = true;
        }
    }

    private resetProgressState(): void {
        this.resetTimer();
        this.countdownEnded = false;
        this.confirmRequest = false;
        this.isConfirming = false;
    }

    /**
     * Emits confirmPurchase at most once per confirmation. The parent handlers await
     * several checks before progressData replaces the Confirm button, so without this
     * guard every extra click would start a concurrent transaction flow.
     */
    onConfirm(): void {
        if (this.isConfirming || this.hasInactiveItems) return;
        this.isConfirming = true;
        this.confirmPurchase.emit();
    }

    allStepsCompleted(): boolean {
        // if progressData is null, undefined, or empty object → false
        if (
            !this.progressData ||
            !this.progressData.steps ||
            this.progressData.steps.length === 0
        ) {
            return false;
        }
    
        // check if every step status is 2
        return this.progressData.steps.every((step: any) => step?.status === 2);
    }

    /**
     * Starts the countdown timer with a total duration in seconds.
     * The method calculates the end time based on the current time and the total duration.
     * It then sets up an interval that updates the remaining time every 10 milliseconds,
     * formatting the time into minutes, seconds, and milliseconds.
     * If the countdown reaches zero, the timer stops.
     *
     * @param {number} totalDurationInS
     *
     */
    private startCountDown(totalDurationInS: number) {
        this.totalDurationInS = totalDurationInS;
        this.totalDurationInMs = this.totalDurationInS * 1000;
        this.endTime = Date.now() + this.totalDurationInMs;
        this.intervalId = setInterval(() => {
            const remainingDuration: number = this.endTime - Date.now();

            if (remainingDuration > 0) {
                this.countdownEnded = false;
                const minutes = Math.floor(
                    (remainingDuration % (1000 * 60 * 60)) / (1000 * 60)
                );
                const seconds = Math.floor(
                    (remainingDuration % (1000 * 60)) / 1000
                );

                this.minutes = String(minutes).padStart(2, '0');
                this.seconds = String(seconds).padStart(2, '0');
            } else {
                this.resetTimer();
                this.countdownEnded = true;
            }
        }, 10);
    }

    /**
     * Resets the countdown timer to its initial state.
     * The method sets the minutes, seconds, and milliseconds back to '00',
     * clears the interval that was set to update the countdown,
     * and marks the countdown as ended.
     *
     */
    private resetTimer() {
        this.minutes = '00';
        this.seconds = '00';
        this.milliseconds = '00';
        clearInterval(this.intervalId);
    }

    /** Cancel and foreclose confirmations have no terms list; every other type shows one. */
    get hasTerms(): boolean {
        const type = this.confirmationData?.confirmation_type;
        return type !== ConfirmationTypeEnum.FORECLOSE && type !== ConfirmationTypeEnum.CANCEL;
    }

    /** Header of the review step: the flow's own title, else one per confirmation type. */
    get reviewTitle(): string {
        return this.confirmationData?.review_title
            || REVIEW_TITLES[this.confirmationData?.confirmation_type]
            || 'Review and confirm.';
    }

    /** Header while processing: the action's name (progressData.actionTitle), else the generic title. */
    get headerTitle(): string {
        return this.progressData?.actionTitle || this.progressData?.title || '';
    }

    get isFailed(): boolean {
        return !!this.progressData?.failed
            || (this.progressData?.steps || []).some((step: any) => step?.status === 3);
    }

    /** "Step 2 of 3.", "Done." or "Failed." for the right side of the processing header. */
    get stepStatusLabel(): string {
        const steps: any[] = this.progressData?.steps || [];
        if (steps.length === 0) return '';
        if (this.isFailed) return 'Failed.';
        if (this.allStepsCompleted()) return 'Done.';
        const activeIndex = steps.findIndex((step: any) => step?.status === 1);
        const position = activeIndex >= 0 ? activeIndex + 1 : steps.filter((step: any) => step?.status === 2).length + 1;
        return `Step ${Math.min(position, steps.length)} of ${steps.length}.`;
    }

    /** One summary per asset in confirmationData.image (entries are { image: nft } once the modal has seen them). */
    get assetSummaries(): ProgressAssetSummary[] {
        return (this.confirmationData?.image || []).map((entry: any) => summariseProgressAsset(entry?.image ?? entry));
    }

    /** Heading of the terms list. */
    get termsHeading(): string {
        const type = this.confirmationData?.confirmation_type;
        if (LOAN_TERM_TYPES.includes(type)) return this.confirmationData?.isCounter ? 'Loan offer terms.' : 'Loan terms.';
        switch (type) {
            case ConfirmationTypeEnum.SALE: return 'Sell order.';
            case ConfirmationTypeEnum.BUY: return 'Purchase.';
            case ConfirmationTypeEnum.BID:
            case ConfirmationTypeEnum.ACCEPT_BID: return 'Bid.';
            case ConfirmationTypeEnum.RESALE:
            case ConfirmationTypeEnum.RESALE_COUNTER: return 'Resale offer.';
            default: return '';
        }
    }

    /**
     * The terms list, built from loanRequestData with the same fields and show-conditions the
     * template used to list inline (desktop and phone each had a copy).
     */
    get termRows(): ConfirmationTermRow[] {
        const data = this.loanRequestData;
        const type = this.confirmationData?.confirmation_type;
        const rows: ConfirmationTermRow[] = [];
        const days = (value: any) => `${value} ${value > 1 ? 'days' : 'day'}`;
        if (LOAN_TERM_TYPES.includes(type)) {
            const symbol = data?.currency_data?.display_symbol || data?.currency?.display_symbol
                || data?.loan_request_data?.currency_data?.display_symbol;
            const money = (value: any) => this.currencyAmount.transform(value, symbol);
            if (data?.requested_loan_amount) {
                rows.push({ label: this.confirmationData?.isCounter ? 'Loan amount offered.' : 'Loan amount requested.', value: money(data.requested_loan_amount) });
            }
            if (data?.loan_duration_days) rows.push({ label: 'Loan duration.', value: days(data.loan_duration_days) });
            if (data?.loan_percentage) rows.push({ label: 'APR.', value: `${data.loan_percentage}%` });
            if (data && (data.hasOwnProperty('emi_option') || data.hasOwnProperty('interest_repayment'))) {
                rows.push({ label: 'Interest repayment.', value: (data.emi_option || data.interest_repayment) ? 'Monthly' : 'End of loan' });
            }
            if (data?.loan_request_data?.start_date) rows.push({ label: 'Loan start date.', value: this.readableDate.transform(data.loan_request_data.start_date) });
            if (data?.loan_request_data?.end_date) rows.push({ label: 'Loan end date.', value: this.readableDate.transform(data.loan_request_data.end_date) });
            rows.push({ label: 'Allow early repayment.', value: data?.loanType ? 'No' : 'Yes' });
            if (data?.expired_days && type === ConfirmationTypeEnum.COUNTER) rows.push({ label: 'Offer expires in.', value: days(data.expired_days) });
            if (data?.interest_amount) rows.push({ label: 'Interest amount.', value: money(data.interest_amount) });
            if (data?.monthly_payment) rows.push({ label: 'Monthly payment.', value: money(data.monthly_payment) });
            if (data?.emi_option) rows.push({ label: 'Estimated monthly repayment.', value: money(data.emiPermonth) });
            if (data?.total_amount) rows.push({ label: 'Total amount.', value: money(data.total_amount), isTotal: true });
            return rows;
        }
        const symbol = data?.currency_data?.display_symbol || data?.currency?.display_symbol;
        const money = (value: any, currency: string = symbol) => this.currencyAmount.transform(value, currency);
        // Resale amounts use the loan's currency; the image's currency and "$" were the old fallbacks.
        const resaleSymbol = this.confirmationData?.currency_symbol
            || this.confirmationData?.image?.[0]?.image?.currency?.display_symbol || '$';
        switch (type) {
            case ConfirmationTypeEnum.SALE:
                if (data?.price) rows.push({ label: 'Sale price.', value: money(data.price), isTotal: true });
                break;
            case ConfirmationTypeEnum.RESALE:
                if (data?.resale_amount || data?.loan_sell_amount) {
                    rows.push({ label: 'Offer.', value: money(data.resale_amount ?? data.loan_sell_amount, resaleSymbol), isTotal: true });
                }
                break;
            case ConfirmationTypeEnum.RESALE_COUNTER:
                if (data?.loan_sell_amount) rows.push({ label: 'Offer.', value: money(data.loan_sell_amount, resaleSymbol) });
                if (data?.expired_days) rows.push({ label: 'Expiry days.', value: days(data.expired_days) });
                break;
            case ConfirmationTypeEnum.BUY:
                if (data?.totalItemValue) rows.push({ label: 'Asset price.', value: money(data.totalItemValue) });
                if (data?.platformFee) rows.push({ label: 'Platform fee.', value: money(data.platformFee) });
                if (data?.price) rows.push({ label: 'Total asset cost.', value: money(data.price), isTotal: true });
                break;
            case ConfirmationTypeEnum.BID:
                if (data?.amount) rows.push({ label: 'Bidding price.', value: money(data.amount) });
                if (data?.expired_days) rows.push({ label: 'Offer expires in.', value: days(data.expired_days) });
                break;
            case ConfirmationTypeEnum.ACCEPT_BID:
                if (data?.amount) rows.push({ label: 'Sale bid price.', value: money(data.amount), isTotal: true });
                break;
        }
        return rows;
    }

    /** The details box the flow passed (progressData.details), for types without a terms list. */
    get hasDetails(): boolean {
        return !this.hasTerms && Array.isArray(this.progressData?.details?.rows) && this.progressData.details.rows.length > 0;
    }

    /** Custodial users: the platform completes the steps, so a custody note holds the countdown. */
    get showCustodyNote(): boolean {
        return this.isRegulated && this.showEstimation && this.confirmRequest && !this.allStepsCompleted() && !this.isFailed;
    }

    /** Self-custody users who reach this modal (the cart) sign each step in their own wallet. */
    get showWalletNote(): boolean {
        return !this.isRegulated && this.isDataLoaded && !this.allStepsCompleted() && !this.isFailed;
    }

    get showConfirmButton() {
        return (
            !this.progressData || Object.keys(this.progressData).length === 0
        );
    }

    get showOkayButton() {
        return (
            this.progressData?.failed ||
            (this.progressData?.currentStep ===
                this.progressData?.steps?.length &&
                [2, 3].includes(
                    this.progressData?.steps[this.progressData?.currentStep - 1]
                        ?.status
                ) &&
                !this.progressData?.showButton)
        );
    }

    ngOnDestroy(): void {
        this.resetTimer();
        // Unsubscribe from modal events to prevent memory leaks
        if (this.modalHiddenSubscription) {
            this.modalHiddenSubscription.unsubscribe();
            this.modalHiddenSubscription = undefined;
        }
        this.hasSubscribedToModalEvents = false;
    }

    /* 
    * Emits the closeModal event and resets progressData
    */
    emitCloseModal() {
        this.resetProgressState();
        this.closeModal.emit();
        setTimeout(() => {
            this.progressData = {};
        }, 200);
    }
}


interface sendvalue {
  status: boolean,
  action: string
}
export enum action {
  Sale = "sell",
  Borrow = 'borrow',
  ForceClose = 'forceclose',
  Transfer = 'transfer',
  Redeme = 'redeme',
  SaleBid = 'sale_bid',
  CounterOffer = 'counter_offer',
  AcceptOpenOffer = 'accept_open_offer',
  CounterOpenOffer = 'counter_open_offer'

}
